<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/authStore'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Card from 'primevue/card'
import Message from 'primevue/message'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')
const tenantSlug = ref<string | undefined>()

// Step state: 'email' or 'methods'
const step = ref<'email' | 'methods'>('email')
const signInMethods = ref<string[]>([])
const samlProviderIds = ref<string[]>([])
const fetchingMethods = ref(false)

// Extract tenant slug from URL if present
onMounted(() => {
  if (route.params.slug) {
    tenantSlug.value = route.params.slug as string
  }
})

async function handleEmailSubmit() {
  fetchingMethods.value = true
  error.value = ''

  try {
    console.log('🔐 LoginView: Fetching sign-in methods', {
      email: email.value,
      tenantSlug: tenantSlug.value,
    })
    const result = await authStore.getSignInMethods(email.value, tenantSlug.value)
    console.log('🔐 LoginView: Sign-in methods received', result)

    // Allow sign-in if we have methods OR SAML providers
    // SAML providers might not show up in fetchSignInMethodsForEmail but are still valid
    if (result.methods.length === 0 && result.samlProviderIds.length === 0) {
      error.value = 'No sign-in methods available for this email address'
      return
    }

    signInMethods.value = result.methods
    samlProviderIds.value = result.samlProviderIds
    step.value = 'methods'
  } catch (e: any) {
    console.error('🔐 LoginView: Error fetching sign-in methods', e)
    error.value = e.message || 'Failed to fetch sign-in methods'
  } finally {
    fetchingMethods.value = false
  }
}

async function handlePasswordLogin() {
  loading.value = true
  error.value = ''

  try {
    console.log('🔐 LoginView: Starting password login', {
      email: email.value,
      tenantSlug: tenantSlug.value,
    })
    await authStore.login(email.value, password.value, tenantSlug.value)
    console.log('🔐 LoginView: Login call completed')

    // Wait for auth state to be fully loaded before redirecting
    await waitForAuthState()

    console.log('🔐 LoginView: Redirecting to /')
    router.push('/')
  } catch (e: any) {
    console.error('🔐 LoginView: Login failed', e)
    error.value = e.message || 'Failed to login'
  } finally {
    loading.value = false
  }
}

async function handleGoogleSignIn() {
  loading.value = true
  error.value = ''

  try {
    console.log('🔐 LoginView: Starting Google sign-in', {
      email: email.value,
      tenantSlug: tenantSlug.value,
    })
    await authStore.signInWithGoogle(email.value, tenantSlug.value)
    console.log('🔐 LoginView: Google sign-in completed')

    // Wait for auth state to be fully loaded before redirecting
    await waitForAuthState()

    console.log('🔐 LoginView: Redirecting to /')
    router.push('/')
  } catch (e: any) {
    console.error('🔐 LoginView: Google sign-in failed', e)
    error.value = e.message || 'Failed to sign in with Google'
  } finally {
    loading.value = false
  }
}

async function handleSAMLSignIn(samlProviderId: string) {
  loading.value = true
  error.value = ''

  try {
    console.log('🔐 LoginView: Starting SAML sign-in', {
      email: email.value,
      samlProviderId,
      tenantSlug: tenantSlug.value,
    })
    await authStore.signInWithSAML(email.value, samlProviderId, tenantSlug.value)
    console.log('🔐 LoginView: SAML sign-in redirect initiated')
    // Note: signInWithRedirect will redirect the page, so we won't reach the code below
    // The redirect will be handled by the router guard when the user returns
  } catch (e: any) {
    console.error('🔐 LoginView: SAML sign-in failed', e)
    error.value = e.message || 'Failed to sign in with SAML'
    loading.value = false
  }
}

function getMethodDisplayName(method: string): string {
  const methodNames: Record<string, string> = {
    password: 'Password',
    'google.com': 'Google',
  }
  // For SAML providers, extract a friendly name from the provider ID
  // e.g., "saml.acme-sso" -> "Acme SSO"
  if (method.startsWith('saml.')) {
    const parts = method.replace('saml.', '').split('-')
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
  }
  return methodNames[method] || method
}

function getMethodIcon(method: string): string {
  const methodIcons: Record<string, string> = {
    password: 'pi pi-lock',
    'google.com': 'pi pi-google',
  }
  return methodIcons[method] || 'pi pi-sign-in'
}

function backToEmail() {
  step.value = 'email'
  password.value = ''
  signInMethods.value = []
  samlProviderIds.value = []
  error.value = ''
}

async function waitForAuthState() {
  console.log('🔐 LoginView: Waiting for auth state to be ready...')
  await new Promise((resolve) => {
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait

    const checkAuth = () => {
      attempts++
      console.log(`🔐 LoginView: Auth check attempt ${attempts}`, {
        loading: authStore.loading,
        isAuthenticated: authStore.isAuthenticated,
        hasPersistedAuth: authStore.hasPersistedAuth,
        isSaasAdmin: authStore.isSaasAdmin,
        hasAuthTenant: authStore.hasAuthTenant,
        claims: authStore.claims,
      })

      // Wait for auth to be ready AND claims to be loaded (especially auth_tenant)
      if (
        !authStore.loading &&
        authStore.isAuthenticated &&
        (authStore.hasAuthTenant || authStore.isSaasAdmin)
      ) {
        console.log('🔐 LoginView: Auth state and claims ready, proceeding with redirect')
        resolve(true)
      } else if (attempts >= maxAttempts) {
        console.log('🔐 LoginView: Max attempts reached, proceeding anyway')
        resolve(true)
      } else {
        setTimeout(checkAuth, 100)
      }
    }
    checkAuth()
  })
}
</script>

<template>
  <div class="login-container">
    <Card class="login-card">
      <template #title>
        <h1 class="m-0">Login</h1>
      </template>
      <template #content>
        <div v-if="tenantSlug" class="mb-3">
          <Message severity="info" :closable="false">
            Logging into tenant: <strong>{{ tenantSlug }}</strong>
          </Message>
        </div>

        <!-- Step 1: Email Input -->
        <div v-if="step === 'email'">
          <div class="field mb-3">
            <label for="email" class="block mb-2">Email</label>
            <InputText
              id="email"
              v-model="email"
              type="email"
              placeholder="Enter your email"
              class="w-full"
              :disabled="fetchingMethods"
              @keyup.enter="handleEmailSubmit"
            />
          </div>

          <div v-if="error" class="mb-3">
            <Message severity="error" :closable="false">{{ error }}</Message>
          </div>

          <Button
            label="Continue"
            icon="pi pi-arrow-right"
            class="w-full"
            :loading="fetchingMethods"
            @click="handleEmailSubmit"
          />
        </div>

        <!-- Step 2: Sign-in Methods -->
        <div v-if="step === 'methods'">
          <div class="mb-3">
            <Message severity="info" :closable="false">
              Sign in as: <strong>{{ email }}</strong>
            </Message>
          </div>

          <div v-if="error" class="mb-3">
            <Message severity="error" :closable="false">{{ error }}</Message>
          </div>

          <!-- Password Sign-in -->
          <div v-if="signInMethods.includes('password')" class="mb-3">
            <div class="field mb-2">
              <label for="password" class="block mb-2">Password</label>
              <Password
                id="password"
                v-model="password"
                placeholder="Enter your password"
                class="w-full"
                :disabled="loading"
                :feedback="false"
                toggle-mask
                @keyup.enter="handlePasswordLogin"
              />
            </div>
            <Button
              :label="loading ? 'Signing in...' : 'Sign in with Password'"
              icon="pi pi-lock"
              class="w-full"
              :loading="loading"
              @click="handlePasswordLogin"
            />
          </div>

          <!-- Google Sign-in -->
          <div v-if="signInMethods.includes('google.com')" class="mb-3">
            <Button
              label="Sign in with Google"
              icon="pi pi-google"
              class="w-full p-button-outlined"
              :loading="loading"
              @click="handleGoogleSignIn"
            />
          </div>

          <!-- SAML Sign-in -->
          <div v-for="samlProviderId in samlProviderIds" :key="samlProviderId" class="mb-3">
            <Button
              :label="`Sign in with ${getMethodDisplayName(samlProviderId)}`"
              :icon="getMethodIcon(samlProviderId)"
              class="w-full p-button-outlined"
              :loading="loading"
              @click="handleSAMLSignIn(samlProviderId)"
            />
          </div>

          <!-- Other sign-in methods -->
          <div
            v-for="method in signInMethods.filter(
              (m) => m !== 'password' && m !== 'google.com' && !m.startsWith('saml.'),
            )"
            :key="method"
            class="mb-3"
          >
            <Button
              :label="`Sign in with ${getMethodDisplayName(method)}`"
              :icon="getMethodIcon(method)"
              class="w-full p-button-outlined"
              :loading="loading"
              disabled
            />
          </div>

          <Button
            label="Back"
            icon="pi pi-arrow-left"
            class="w-full p-button-text"
            :disabled="loading"
            @click="backToEmail"
          />
        </div>
      </template>
    </Card>
  </div>
</template>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.login-card {
  width: 100%;
  max-width: 400px;
}
</style>
